<template>
  <div class="aco-grid">
    <div class="aco-info-box">
      <b-card>
        <margin-fields :fields="marginFields()"/>
        <b-form-group id="updateEmailButton">
          <b-button type="submit" block v-if="showUpdateEmailButton" v-b-modal.update-customer-email-modal>
            {{ $t('updateCustomerEmail') }}
          </b-button>
          <update-customer-email/>
        </b-form-group>
        <b-button id="licenseSearch" type="submit" block :disabled="disableLicenseSearchButton" v-on:click="licenseSearchByCustomerOrgNumber()">
          {{ $t('licenseSearch') }}
        </b-button>
        <b-button id="showEvents" type="submit" block :disabled="isShowEventButtonEnabled" v-on:click="showEvents()">{{ $t('showEvents') }}</b-button>
        <b-button type="submit" :disabled="!canRestoreOrder" @click="restoreOrder()" block>{{ $t('restoreOrder') }}</b-button>
        <restore-order-modal ref="confirmRestoreOrder"/>
        <show-events ref="showEvents" :events="events"/>
      </b-card>
    </div>

    <div>
      <div class="aco-details-box">
        <b-col class="aco-details">
          <details-form :fields="orderDetailsFields()" :state-object="order"/>
          <details-form v-if="order.customerOrganizationNumber" :fields="customerDetailsFields()" :state-object="order"/>
          <details-form v-if="order.customerOrganizationNumber == null" :fields="customerUserIdDetailsFields()" :state-object="order"/>
          <details-form v-if="order.buyerOrganizationNumber" :fields="buyerDetailsFields()" :state-object="order"/>
        </b-col>
      </div>

      <div class="aco-details-box">
        <b-col class="aco-details">
          <b-row>
            <b-col>
              <h5>{{ $t('orderLines') }}</h5>
            </b-col>
          </b-row>
          <b-row>
            <b-table striped hover :items="order.orderLines" :fields="orderLineFields" class="bg-white table-condensed mx-3">
              <template v-slot:head(includeInInvoice)="data">
                {{ data.label }}
                <font-awesome-icon id="includeInInvoiceTip" icon="info-circle"/>
              </template>

              <template v-slot:cell(update)="row">
                <b-button size="sm" id="show-btn" v-if="showUpdateButton(row)" @click="showUpdateModal(row)">
                  {{ $t('update') }}
                </b-button>
              </template>

              <template v-slot:row-details="row">
                <b-row>
                  <b-col md="1"><b>{{ $t('description') }}</b></b-col>
                  <b-col>{{ row.item.statusDescription }}</b-col>
                </b-row>
              </template>

              <template v-slot:cell(licenseSearch)="row">
                <b-button size="sm" id="licenseSearchByOrder-btn" @click="licenseSearchByOrder(row)">
                  {{ $t('licenseSearch') }}
                </b-button>
              </template>

            </b-table>
          </b-row>
        </b-col>
      </div>
    </div>

    <b-modal id="update-modal" ref="update-modal" no-close-on-backdrop no-stacking @close="resetModal">
      <template slot="modal-title"> {{ $t('updateOrderLineMessage') }}</template>
      <form ref="form" v-on:submit.prevent>
        <h6><label class="mb-4">{{ $t('currentStatus') }}</label> : {{ translateAlternatives('constants', status) }}</h6>

        <b-row class="mb-4">
          <b-col cols="3">{{ $t('newStatus') }}</b-col>
          <b-col>
            <b-form-select v-model="updateRequest.status" :options="statusOption" class="mb-3" @change="onChange()" autofocus>
              <template slot="first">
                <option disabled :value="null">{{ $t('selectStatus') }}</option>
              </template>
            </b-form-select>
          </b-col>
        </b-row>
        <b-row class="mb-4">
          <b-col cols="3">{{ $t('message') }}</b-col>
          <b-col>
            <b-form-input type="text" v-model="updateRequest.description"/>
          </b-col>
        </b-row>
      </form>

      <div slot="modal-footer" class="w-50">
        <b-button variant="primary" b-button v-b-modal.confirmation :disabled=disableUpdateButton>
          {{ $t('update') }}
        </b-button>
        <b-button variant="secondary" class="float-lg-right" v-on:click="resetModal">
          {{ $t('cancel') }}
        </b-button>
      </div>
    </b-modal>

    <b-modal id="confirmation" @hidden="resetModal" @ok="updateLineStatus" ok-variant="outline-primary"
             no-close-on-esc no-close-on-backdrop>
      <template slot="modal-title">{{ $t('confirmationTitle') }}</template>
      <template slot="modal-ok">{{ $t('update') }}</template>
      <template slot="modal-cancel"> {{ $t('cancel') }}</template>
      <template slot="default">{{ $t('confirmation') }}</template>
    </b-modal>

    <b-tooltip target="updateEmailButton">{{ $t('toolTip.updateEmailButtonTip') }}</b-tooltip>
    <b-tooltip target="includeInInvoiceTip">{{ $t('toolTip.includeInInvoice') }}</b-tooltip>
    <b-tooltip target="licenseSearch">{{ $t('toolTip.licenseSearchFromOrder') }}</b-tooltip>
  </div>
</template>

<script>
  import BaseComponent from '../../components/BaseComponent'
  import { mapGetters } from 'vuex'
  import UpdateCustomerEmail from '../UsersPage/UpdateCustomerEmail'
  import MarginFields from '../../components/MarginFields'
  import DetailsForm from '../../components/DetailsForm'
  import ShowEvents from '../../components/ShowEvents'
  import RestoreOrderModal from '@/views/OrdersPage/RestoreOrderModal.vue';
  import { isAdmin } from '@/services/PermissionService';

  export default {
    extends: BaseComponent,
    components: {
      UpdateCustomerEmail,
      MarginFields,
      DetailsForm,
      ShowEvents,
      RestoreOrderModal,
    },

    data () {
      return {
        statusOption: [],
        updateRequest: {
          status: null,
          description: null
        },
        sourceLineId: null,
        orderId: null,
        status: null,
        isButtonDisabled: true
      }
    },
    mounted () {
      this.$store.dispatch('orders/getOrder', this.$route.params.id)
      const objectReference = 'order:orderId:' + this.$route.params.id
      this.$store.dispatch('orders/getObjectReference', objectReference)
    },
    computed: {
      ...mapGetters('orders', ['order', 'isShowEventButtonEnabled', 'events']),
      ...mapGetters('oidcStore', ['oidcUser']),
      orderLineFields () {
        return [
          { key: 'sourceLineId', label: this.$t('line') },
          { key: 'status', label: this.$t('status'), formatter: value => this.translateAlternatives('constants', value) },
          { key: 'productEan', label: this.$t('ean') },
          { key: 'productType', label: this.$t('type'), formatter: value => this.translateAlternatives('productTypes', value) },
          { key: 'productName', label: this.$t('name') },
          { key: 'quantity', label: this.$t('quantity') },
          { key: 'validFrom', label: this.$t('from') },
          { key: 'validTo', label: this.$t('to') },
          { key: 'includeInInvoice', label: this.$t('includeInInvoice'), formatter: value => this.translateAlternatives('booleans', value) },
          { key: 'bundleEan', label: this.$t('bundleEan'), formatter: 'translateNullValue' },
          { key: 'buyerLineId', label: this.$t('buyerLineId'), formatter: 'translateNullValue' },
          { key: 'update', label: '' },
          { key: 'licenseSearch', label: '' }
        ]
      },
      disableUpdateButton () {
        return (this.updateRequest.status === null && this.isButtonDisabled === true)
      },
      showUpdateEmailButton () {
        return (this.order.orderStatus === 'NEW' && this.order.orderSource === 'ACO_MAGENTO')
      },
      disableLicenseSearchButton () {
        return (this.order.customerOrganizationNumber === null && this.order.customerUserId === null)
      },
      canRestoreOrder() {
        return isAdmin(this.oidcUser?.username);
      },
    },
    methods: {
      updateLineStatus () {
        let me = this
        this.$store.dispatch('orders/updateOrderLineStatus', {
          orderId: this.$route.params.id,
          orderLineId: this.sourceLineId,
          updateRequest: this.updateRequest
        }).then(function () {
          me.$root.$emit('bv::hide::update-modal', 'confirmation')
          me.resetModal()
        })
      },
      setModelValues (orderId, line, status) {
        this.orderId = orderId
        this.sourceLineId = line
        this.status = status
        if (this.status === 'NEW') {
          this.statusOption.push({ value: 'REJECTED', text: this.$t('constants.REJECTED') })
          this.statusOption.push({ value: 'FINISHED', text: this.$t('constants.FINISHED') })
        }
        if (this.status === 'FULFILLED') {
          this.statusOption.push({ value: 'FINISHED', text: this.$t('constants.FINISHED') })
        }
      },
      onChange () {
        this.isButtonDisabled = false
      },
      showUpdateButton (row) {
        return (row.item.status === 'NEW' || row.item.status === 'FULFILLED')
      },
      showUpdateModal (row) {
        this.setModelValues(this.order.orderId, row.item.sourceLineId, row.item.status)
        this.$refs['update-modal'].show()
      },
      resetModal () {
        this.statusOption = []
        this.status = null
        this.isButtonDisabled = true
        this.updateRequest = {
          status: null,
          description: null
        }
        this.$refs['update-modal'].hide()
      },
      licenseSearchByCustomerOrgNumber () {
        let payload = {
          customerOrganizationNumber: this.order.customerOrganizationNumber,
          customerUserId: this.order.customerUserId
        }
        this.$store.dispatch('licenses/resetFilter')
        this.$store.dispatch('licenses/getLicensesByCustomerOrgNumberOrUserIdOrEan', payload)
          .then(() => this.$router.push('/licenses'))
      },
      licenseSearchByOrder (row) {
        let payload = {
          orderId: this.order.orderId,
          sourceOrderLineId: row.item.sourceLineId
        }
        this.$store.dispatch('licenses/resetFilter')
        this.$store.dispatch('licenses/getLicenseByOrderDetails', payload)
          .then(() => this.$router.push('/licenses'))
      },
      marginFields () {
        return [
          { name: 'status', displayName: this.$t('status'), value: this.translateAlternatives('constants', this.order.orderStatus) },
          { name: 'source', displayName: this.$t('source'), value: this.translateAlternatives('orderSources', this.order.orderSource) },
          { name: 'paymentMethod', displayName: this.$t('paymentMethod'), value: this.translateAlternatives('paymentType', this.order.orderPayment) },
          { name: 'created', displayName: this.$t('created'), value: this.formatTimestamp(this.order.created) },
          { name: 'modified', displayName: this.$t('modified'), value: this.formatTimestamp(this.order.modified) }
        ]
      },

      orderDetailsFields () {
        return [
          { type: 'header', backButton: true, width: 12, displayName: this.$t('orderInformation') },
          { type: 'text', name: 'orderId', readonly: true, width: 4 },
          { type: 'text', name: 'orderSourceId', readonly: true, width: 4 },
          { type: 'header', width: 12, displayName: this.$t('customer') },
        ]
      },
      customerDetailsFields () {
        return [
          { type: 'text', name: 'customerOrganizationNumber', readonly: true, width: 4, displayName: this.$t('organizationNumber') },
          { type: 'text', name: 'customerOrganizationName', readonly: true, width: 4, displayName: this.$t('name') },
          { type: 'text', name: 'customerAcoId', readonly: true, width: 4, displayName: this.$t('acoCustomerId') },
          { type: 'text', name: 'customerContactName', readonly: true, width: 4, displayName: this.$t('contactName') },
          { type: 'text', name: 'customerContactEmail', readonly: true, width: 4, displayName: this.$t('email') },
          { type: 'text', name: 'customerReference', readonly: true, width: 4, displayName: this.$t('reference') },
        ]
      },
      customerUserIdDetailsFields () {
        return [
          { type: 'text', name: 'customerContactName', readonly: true, width: 3, displayName: this.$t('name') },
          { type: 'text', name: 'customerContactEmail', readonly: true, width: 3, displayName: this.$t('email') },
          { type: 'text', name: 'customerUserId', readonly: true, width: 3, displayName: this.$t('userId') },
          { type: 'text', name: 'customerUsername', readonly: true, width: 3, displayName: this.$t('username') }
        ]
      },
      buyerDetailsFields () {
        return [
          { type: 'header', width: 12, displayName: this.$t('buyer') },
          { type: 'text', name: 'buyerOrganizationNumber', readonly: true, width: 4, displayName: this.$t('organizationNumber') },
          { type: 'text', name: 'buyerOrganizationName', readonly: true, width: 4, displayName: this.$t('name') },
          { type: 'text', name: 'buyerAcoId', readonly: true, width: 4, displayName: this.$t('acoCustomerId') },
          { type: 'text', name: 'buyerContactName', readonly: true, width: 4, displayName: this.$t('contactName') },
          { type: 'text', name: 'buyerContactEmail', readonly: true, width: 4, displayName: this.$t('email') },
        ]
      },
      showEvents () {
        this.$root.$emit('bv::show::modal', 'showEvents')
      },
      restoreOrder () {
        this.$root.$emit('bv::show::modal', 'confirmRestoreOrder')
      },
    }
  }
</script>
